import Button, { ButtonVariant } from '@components/Button';
import Markdown from '@components/Markdown';
import Modal from '@components/Modal';
import {
  AgentReassignNotificationDetail,
  NotificationCode,
} from '@customTypes/api/Notification';
import { Dialog } from '@headlessui/react';
import useLocalStorage, { STORAGE_KEY } from '@hooks/useLocalStorage';
import pathnames from '@src/app/routes/pathnames';
import { useNotificationContext } from '@src/context/NotificationContext';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

interface AutoAssignNotificationProps {
  onNext: () => void;
}

const AutoAssignNotification: React.FC<AutoAssignNotificationProps> = ({
  onNext,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { getStorage, setStorage } = useLocalStorage();

  const lastAutoAssignRead = getStorage(STORAGE_KEY.LAST_AUTO_ASSIGN_READ());

  const [notification, setNotification] =
    useState<AgentReassignNotificationDetail | null>(null);

  const { notificationQuery } = useNotificationContext();

  const handleClose = () => {
    setStorage(STORAGE_KEY.LAST_AUTO_ASSIGN_READ(), {
      id: notification?.id,
      date: dayjs().format('DD-MM-YYYY'),
    });
    setNotification(null);
    onNext();
  };

  const handleSeeAgent = () => {
    handleClose();
    history.push(pathnames.agentManagement());
  };

  useEffect(() => {
    const notificationData = notificationQuery.data?.notifications.find(
      notification => notification.code === NotificationCode.AgentReassign,
    );

    const lastNotification =
      notificationData?.total_unread &&
      (notificationData?.details?.filter(
        detail => !detail.read,
      )?.[0] as AgentReassignNotificationDetail);

    if (
      lastNotification &&
      (lastAutoAssignRead?.id !== lastNotification.id ||
        (lastAutoAssignRead?.id === lastNotification.id &&
          lastAutoAssignRead?.date !== dayjs().format('DD-MM-YYYY')))
    ) {
      setNotification(lastNotification);
    }
  }, [
    lastAutoAssignRead?.date,
    lastAutoAssignRead?.id,
    notificationQuery.data?.notifications,
  ]);

  return (
    <Modal
      open={!!notification}
      contentClassName="w-full max-w-[320px] lg:max-w-[536px]"
      dataTestId="auto-assign-notification"
    >
      <Dialog.Title
        as="h1"
        className="text-xl sm:text-2xl mb-4 text-center font-semibold"
        data-testid="auto-assign-notification-title"
      >
        {t('notification.autoAssign.title')}
      </Dialog.Title>
      <div
        className="mb-4 text-base sm:text-lg text-center"
        data-testid="auto-assign-notification-content"
      >
        <Markdown>
          {t('notification.autoAssign.description', {
            agentCount: notification?.metadata?.agents_count,
            prevAREType: notification?.metadata?.product_type,
            prevAREName: notification?.metadata?.old_are_name,
            prevAREMarketingId: notification?.metadata?.old_are_marketing_id,
            newAREName: notification?.metadata?.new_are_name,
            newAREMarketingId: notification?.metadata?.new_are_marketing_id,
          })}
        </Markdown>
      </div>
      <div className="flex sm:flex-row flex-col gap-4">
        <Button
          variant={ButtonVariant.Secondary}
          className="w-full"
          onClick={handleClose}
          dataTestId="auto-assign-notification-close-btn"
        >
          {t('commons.close')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          className="w-full"
          onClick={handleSeeAgent}
          dataTestId="auto-assign-notification-see-agent-btn"
        >
          {t('commons.seeAgent')}
        </Button>
      </div>
    </Modal>
  );
};

export default AutoAssignNotification;
